<template>
  <div>
    <intro-email-gate
      :show="showIntroGate"
      :hero-image="heroImage"
      :body-text="introBodyText || undefined"
      @close="showIntroGate = false"
      @start="onIntroStart"
    />
    <videocall-login-popup
      :show="showVideocallLogin"
      :email="videocallPendingEmail"
      :loading="videocallLoggingIn"
      :server-error="videocallLoginError"
      @close="showVideocallLogin = false"
      @login="onVideocallLogin"
    />
    <videocall-register-popup
      :show="showVideocallRegister"
      :email="videocallPendingEmail"
      :loading="videocallRegistering"
      @close="showVideocallRegister = false"
      @register="onVideocallRegister"
    />
    <email-code-popup
      :show="showVideocallEmailCode"
      :email="pendingRegistration ? pendingRegistration.email : ''"
      :brand="emailCodeBrand"
      :busy="videocallRegistering"
      busy-text="Creazione dell'account in corso..."
      @close="onVideocallCodeClosed"
      @verified="onVideocallEmailVerified"
    />
    <login-code-popup
      :show="showVideocallLoginCode"
      :masked-email="videocallLoginCodeEmail"
      :resend-in-sec="videocallLoginCodeResendInSec"
      :flow="videocallLoginCodeFlow"
      @verified="onVideocallLoginCodeVerified"
      @close="onVideocallLoginCodeClosed"
    />
    <book-videocall-popup
      :show="showBookVideocallPopup"
      :uid="videocallUid"
      :client-name="videocallClientName"
      :client-email="videocallClientEmail"
      :default-phone="videocallDefaultPhone"
      :newarc-home-id="newarcHomeId"
      :ad-code="adCode"
      :ad-address="adAddress"
      :ad-id="adId"
      :objectives="objectives"
      :link-objective="linkObjective"
      :link-heading="linkHeading"
      :link-help="linkHelp"
      :source="source"
      :general-emails="generalEmails"
      :single-objective="singleObjective"
      @close="showBookVideocallPopup = false"
    />
  </div>
</template>

<script>
import firebase from "firebase/compat/app";
import { globalStore, getUserDetails, accountExistsForEmail, createVerifiedWebAccount, webAccountErrorReason, currentBrand } from "@/globalStore.js";
import { VIDEOCALL_OBJECTIVES, VIDEOCALL_GENERAL_OBJECTIVE } from "@/services/videocall.js";
import IntroEmailGate from "@/components/Videocall/IntroEmailGate.vue";
import VideocallLoginPopup from "@/components/Videocall/VideocallLoginPopup.vue";
import VideocallRegisterPopup from "@/components/Videocall/VideocallRegisterPopup.vue";
import BookVideocallPopup from "@/components/Videocall/BookVideocallPopup.vue";
import EmailCodePopup from "@/components/EmailCodePopup.vue";
import LoginCodePopup from "@/components/LoginCodePopup.vue";
import { websiteLoginCode, passwordLoginMessage } from "@/services/loginCode.js";
import { recordUserLegalAcceptance } from "@/consent/legalAcceptance.js";

export default {
  name: "VideocallFlow",
  components: {
    IntroEmailGate,
    VideocallLoginPopup,
    VideocallRegisterPopup,
    BookVideocallPopup,
    EmailCodePopup,
    LoginCodePopup,
  },
  props: {
    newarcHomeId: { type: String, default: "" },
    adCode: { type: String, default: "" },
    adAddress: { type: String, default: "" },
    adId: { type: String, default: "" },
    source: { type: String, default: "immaginacasa-ad" },
    objectives: { type: Array, default: () => VIDEOCALL_OBJECTIVES },
    linkObjective: { type: String, default: VIDEOCALL_GENERAL_OBJECTIVE },
    linkHeading: { type: String, default: "Hai già un immobile in mente?" },
    linkHelp: { type: String, default: "Incolla il link dell'annuncio (facoltativo)" },
    generalEmails: { type: Boolean, default: false },
    singleObjective: { type: Boolean, default: false },
    heroImage: { type: String, default: () => require("@/assets/img/videocall/hero.png") },
    introBodyText: { type: String, default: "" },
  },
  data() {
    return {
      showBookVideocallPopup: false,
      showIntroGate: false,
      showVideocallRegister: false,
      // #666: the email is proven with a code before the account is created.
      showVideocallEmailCode: false,
      pendingRegistration: null,
      emailCodeBrand: currentBrand(),
      showVideocallLogin: false,
      videocallRegistering: false,
      videocallLoggingIn: false,
      videocallLoginError: "",
      // #666: the email code step of the password login.
      showVideocallLoginCode: false,
      videocallLoginCodeFlow: null,
      videocallLoginCodeEmail: "",
      videocallLoginCodeResendInSec: 60,
      videocallPendingEmail: "",
      videocallUser: null,
    };
  },
  computed: {
    // Scroll-lock del body mentre uno qualsiasi dei popup del flusso è aperto,
    // come fanno gli altri popup del sito (addBodyOverflowHidden).
    anyPopupOpen() {
      return this.showIntroGate || this.showVideocallLogin || this.showVideocallRegister || this.showBookVideocallPopup;
    },
    // The user driving the booking: the headless videocall user (set via the
    // email gate / register), or the globally logged-in user.
    videocallActiveUser() {
      return this.videocallUser || globalStore.newarcUser || null;
    },
    videocallUid() {
      const authUser = firebase.auth().currentUser;
      if (authUser && authUser.uid) return authUser.uid;
      const u = this.videocallActiveUser;
      return u && u.id ? u.id : "";
    },
    videocallClientName() {
      const u = this.videocallActiveUser;
      return u ? u.getFullName() : "";
    },
    videocallClientEmail() {
      const u = this.videocallActiveUser;
      return u ? u.email : this.videocallPendingEmail;
    },
    videocallDefaultPhone() {
      const u = this.videocallActiveUser;
      return u ? u.phone || "" : "";
    },
  },
  watch: {
    anyPopupOpen(open) {
      document.body.classList.toggle('overflow-hidden', open);
    },
  },
  beforeUnmount() {
    if (this.anyPopupOpen) document.body.classList.remove('overflow-hidden');
  },
  methods: {
    open() {
      if (globalStore.isLoggedIn) {
        this.videocallUser = globalStore.newarcUser;
        this.showBookVideocallPopup = true;
      } else {
        this.showIntroGate = true;
      }
    },
    async onIntroStart(email) {
      this.showIntroGate = false;
      this.showVideocallRegister = false;
      this.showVideocallLogin = false;
      this.videocallLoginError = "";
      this.videocallPendingEmail = email;
      let existing = false;
      try {
        existing = await accountExistsForEmail(email);
      } catch (e) {
        existing = false;
      }
      // A later start (another email) owns the popups now.
      if (this.videocallPendingEmail !== email) return;
      if (existing) {
        this.showVideocallLogin = true;
      } else {
        this.showVideocallRegister = true;
      }
    },
    // #666: the password opens an email code step; the booking opens once
    // the code has signed the session in (onVideocallLoginCodeVerified).
    async onVideocallLogin({ email, password }) {
      if (this.videocallLoggingIn) return;
      this.videocallLoggingIn = true;
      this.videocallLoginError = "";
      try {
        // A new attempt starts clean: nothing of the previous one survives (#861).
        const previous = this.videocallLoginCodeFlow;
        this.videocallLoginCodeFlow = null;
        if (previous) await previous.cancel();
        const flow = websiteLoginCode();
        const result = await flow.start(email, password, currentBrand());
        if (result.status === "unverified") {
          this.videocallLoginError = "Verifica prima la tua email: controlla la tua casella di posta.";
          return;
        }
        if (result.status !== "code") {
          this.videocallLoginError = result.message;
          return;
        }
        this.videocallLoginCodeFlow = flow;
        this.videocallLoginCodeEmail = result.maskedEmail;
        this.videocallLoginCodeResendInSec = result.resendInSec;
        this.showVideocallLogin = false;
        this.showVideocallLoginCode = true;
      } catch (e) {
        this.videocallLoginError = passwordLoginMessage(e);
      } finally {
        this.videocallLoggingIn = false;
      }
    },
    onVideocallLoginCodeClosed() {
      this.showVideocallLoginCode = false;
      if (this.videocallLoginCodeFlow) this.videocallLoginCodeFlow.cancel();
      this.videocallLoginCodeFlow = null;
      this.showVideocallLogin = true;
    },
    async onVideocallLoginCodeVerified() {
      this.showVideocallLoginCode = false;
      this.videocallLoginCodeFlow = null;
      try {
        // The card of the account the code signed in, by uid: a lookup by
        // email misses the cards the server stores with the email lower-cased.
        await getUserDetails();
        const user = globalStore.newarcUser;
        if (!user) throw new Error("users card not found");
        this.setVideocallLoggedIn(user);
        this.videocallUser = user;
        this.showBookVideocallPopup = true;
      } catch (e) {
        this.videocallLoginError = "Si è verificato un errore. Riprova.";
        this.showVideocallLogin = true;
      }
    },
    // #666: the register form first proves the email with a code; the account
    // is created in onVideocallEmailVerified with the proof.
    onVideocallRegister({ firstname, lastname, email, phone }) {
      if (this.videocallRegistering) return;
      this.pendingRegistration = {
        firstname,
        lastname,
        email: email || this.videocallPendingEmail,
        phone: phone || "",
      };
      this.showVideocallRegister = false;
      this.showVideocallEmailCode = true;
    },
    onVideocallCodeClosed() {
      // Back to the form, e.g. to correct the email.
      this.showVideocallEmailCode = false;
      this.showVideocallRegister = true;
    },
    // The code popup stays open (busy) while the account is created, so the
    // user sees progress. The server creates the account (#766) and its custom
    // token signs it in: the booking is written as that user, which the
    // videocallRequests rule requires. Without a password the server picks a
    // random one and sends "Crea la tua password" itself, so this session is
    // the only way in until the user sets their own.
    async onVideocallEmailVerified({ proof }) {
      const pending = this.pendingRegistration;
      if (!pending || this.videocallRegistering) return;
      this.videocallRegistering = true;
      try {
        const { user: authUser, newarcUser: user } = await createVerifiedWebAccount({
          ...pending,
          proof,
          brand: currentBrand(),
        });
        // Prova dell'accettazione delle Condizioni d'uso (#815): mai bloccante.
        recordUserLegalAcceptance({ uid: authUser?.uid, context: "registrazione-videochiamata" });
        this.showVideocallEmailCode = false;
        this.videocallUser = user;
        const signedIn = this.ensureVideocallSession();
        if (!signedIn) {
          // Without a session the booking would be refused: ask to log in.
          this.videocallPendingEmail = pending.email;
          this.videocallLoginError = "Account creato. Imposta la password dal link che ti abbiamo inviato via email, poi accedi per completare la richiesta.";
          this.showVideocallLogin = true;
          return;
        }
        this.setVideocallLoggedIn(user);
        this.showBookVideocallPopup = true;
      } catch (e) {
        this.showVideocallEmailCode = false;
        if (webAccountErrorReason(e) === "email-in-use") {
          // The email has an account after all: log in with it.
          this.videocallPendingEmail = pending.email;
          this.videocallLoginError = "Esiste già un account con questa email: accedi con la tua password.";
          this.showVideocallLogin = true;
          return;
        }
        // Registration failed: back to the form so the user can retry.
        this.showVideocallRegister = true;
      } finally {
        this.videocallRegistering = false;
      }
    },
    // The session kept from sign-up. There is no fallback sign-in: nobody
    // knows the new account's password until the user sets it.
    ensureVideocallSession() {
      const current = firebase.auth().currentUser;
      if (current && current.uid) return true;
      console.error("[videocall] no session after sign-up");
      return false;
    },
    setVideocallLoggedIn(user) {
      globalStore.currentUser = firebase.auth().currentUser;
      globalStore.newarcUser = user;
      globalStore.isLoggedIn = true;
    },
  },
};
</script>
